import { useCallback, useEffect, useRef, useState } from 'react';
import { isSlotPast } from '../utils/slotTime';

const HOVER_DELAY_MS = 280;
const LONG_PRESS_MS = 350;

export type SlotPopoverState = {
  rect: { x: number; y: number; width: number; height: number };
  cursor: { x: number; y: number };
  day: string;
  hour: number;
};

export function useSlotHoverPopover(
  isMobileView: boolean,
  hasSlotSelections: (day: string, hour: number) => boolean
) {
  const [popover, setPopover] = useState<SlotPopoverState | null>(null);
  const hoverTimer = useRef<number | null>(null);
  const longPressTimer = useRef<number | null>(null);
  const popoverVisibleRef = useRef(false);
  const wasHoveringSlotsRef = useRef(false);

  useEffect(() => {
    popoverVisibleRef.current = popover !== null;
  }, [popover]);

  const clearHoverTimer = useCallback(() => {
    if (hoverTimer.current) {
      window.clearTimeout(hoverTimer.current);
      hoverTimer.current = null;
    }
  }, []);

  const openPopoverFor = useCallback(
    (el: HTMLElement, day: string, hour: number, cursor: { x: number; y: number }) => {
      const r = el.getBoundingClientRect();
      setPopover({
        rect: { x: r.left, y: r.top, width: r.width, height: r.height },
        cursor,
        day,
        hour,
      });
    },
    []
  );

  const handleSlotMouseEnter = useCallback(
    (e: React.MouseEvent, day: string, hour: number) => {
      if (isMobileView || isSlotPast(day, hour)) return;
      clearHoverTimer();
      if (!hasSlotSelections(day, hour)) {
        setPopover(null);
        return;
      }
      const target = e.currentTarget as HTMLElement;
      const cursor = { x: e.clientX, y: e.clientY };
      const show = () => openPopoverFor(target, day, hour, cursor);
      const showImmediately = popoverVisibleRef.current || wasHoveringSlotsRef.current;
      wasHoveringSlotsRef.current = true;

      if (showImmediately) {
        show();
        return;
      }

      hoverTimer.current = window.setTimeout(show, HOVER_DELAY_MS);
    },
    [clearHoverTimer, hasSlotSelections, isMobileView, openPopoverFor]
  );

  const handleSlotMouseMove = useCallback(
    (e: React.MouseEvent, day: string, hour: number) => {
      if (isMobileView || isSlotPast(day, hour) || !hasSlotSelections(day, hour)) return;
      const cursor = { x: e.clientX, y: e.clientY };
      setPopover(prev => {
        if (!prev || prev.day !== day || prev.hour !== hour) return prev;
        if (prev.cursor.x === cursor.x && prev.cursor.y === cursor.y) return prev;
        return { ...prev, cursor };
      });
    },
    [hasSlotSelections, isMobileView]
  );

  const handleSlotMouseLeave = useCallback(
    (day: string, hour: number) => {
      clearHoverTimer();
      setPopover(prev => (prev?.day === day && prev?.hour === hour ? null : prev));
    },
    [clearHoverTimer]
  );

  const handleCalendarMouseLeave = useCallback(() => {
    clearHoverTimer();
    wasHoveringSlotsRef.current = false;
    setPopover(null);
  }, [clearHoverTimer]);

  const handleTouchStartCell = useCallback(
    (e: React.TouchEvent, day: string, hour: number) => {
      if (isSlotPast(day, hour) || !hasSlotSelections(day, hour)) return;
      const target = e.currentTarget as HTMLElement;
      const touch = e.touches[0];
      const cursor = touch
        ? { x: touch.clientX, y: touch.clientY }
        : { x: target.getBoundingClientRect().left + target.offsetWidth / 2, y: target.getBoundingClientRect().top + target.offsetHeight / 2 };
      if (longPressTimer.current) window.clearTimeout(longPressTimer.current);
      longPressTimer.current = window.setTimeout(() => {
        openPopoverFor(target, day, hour, cursor);
      }, LONG_PRESS_MS);
    },
    [hasSlotSelections, openPopoverFor]
  );

  const cancelLongPress = useCallback(() => {
    if (longPressTimer.current) {
      window.clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  return {
    popover,
    setPopover,
    openPopoverFor,
    handleSlotMouseEnter,
    handleSlotMouseMove,
    handleSlotMouseLeave,
    handleCalendarMouseLeave,
    handleTouchStartCell,
    cancelLongPress,
  };
}
